import React, { useEffect } from 'react'
import {
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Stack,
  Box,
  Link,
  FormHelperText,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import logoImage from '../../assets/images/logo@3x.webp'
// import { getShippingServiceInstance } from '../../services/axios/Instances/shipping-service-instance'
// import { endpoints } from '../../services/axios/endpoints'
import { accessSscc } from '../../services/axios/functions/list.function'
import AlertModal from '../../components/alert/AlertModal'

const LoginSchema = z.object({
  po: z
    .string({ required_error: 'PO is required' })
    .min(1, { message: 'PO is required' })
    .regex(/^[&.;:/()\s#A-Za-z0-9_-]+$/, 'Invalid PO #. Please check and try again'),
  sscc: z
    .string({ required_error: 'SSCC is required' })
    .min(1, { message: 'Valid SSCC is required' })
    .regex(/^\(\d{2}\)\d{18}$/, 'Invalid SSCC. Please check and try again'),
})

type LoginFormValues = z.infer<typeof LoginSchema>

const Login: React.FC = () => {
  const [characterCount, setCharacterCount] = React.useState(0)
  // const [fullData, setFullData] = React.useState<any>({ po: '', sscc: '' })
  const [showModal, setShowModal] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState({
    msg: '',
    header: '',
    btnText: '',
  })
  const navigate = useNavigate()
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      po: '',
      sscc: '',
    },
  })

  const isButtonDisabled = !watch('po') || !watch('sscc')

  const onSubmit = async (data: LoginFormValues) => {
    // await getShippingServiceInstance().post(endpoints.saveData, {
    //   poNumber: data.po,
    //   sscc: data.sscc,
    //   poNumberValuesString: fullData.po,
    //   ssccValuesString: fullData.sscc,
    // })

    const res = await accessSscc(data.sscc, data.po)

    if (res?.status !== 206) {
      navigate(`/dashboard`, {
        state: data,
      })
    } else {
      setShowModal(true)
      setErrorMessage({
        msg: res?.data?.message,
        header: 'Alert',
        btnText: 'TRY AGAIN',
      })
    }
  }

  useEffect(() => {
    sessionStorage.clear()
  }, [])

  const handleChange = (event: any, field: any) => {
    const value = event.target.value
    if (value?.length == 4 && !value.includes('(')) {
      const newValue = value.replace('(', '').replace(')', '')
      field.onChange(newValue)
    } else if (value?.length == 2 && !value.includes('(')) {
      field.onChange(`(${value})`)
    } else {
      field.onChange(value)
    }
    setCharacterCount(value.length)
  }

  const handlePOChange = (event: any, field: any) => {
    const value = event.target.value.replace(/:{2,}/g, ':').replace(/;{2,}/g, ';')
    field.onChange(value)
  }

  return (
    <Container maxWidth="lg">
      <Stack
        spacing={2}
        paddingY={{ xs: 2, sm: 3 }}
        boxSizing={'border-box'}
        direction="column"
        alignItems="center"
        minHeight={'100vh'}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Box flexGrow={1} display={'flex'} alignItems={'center'}>
          <Paper
            elevation={2}
            sx={{
              py: { xs: 4, sm: 5 },
              px: { xs: 4, sm: 10 },
              maxWidth: { xs: '100%', sm: 320 },
              borderRadius: 3,
              paddingBottom: '0 !important',
            }}
          >
            <Typography variant="h5" align="center" gutterBottom>
              <img
                src={logoImage}
                alt="LifeScience Logistics"
                style={{
                  height: '58px',
                  width: 'auto',
                }}
              />
            </Typography>
            <Typography
              variant="h6"
              align="center"
              gutterBottom
              mb={0}
              color="primary.dark"
              fontFamily={'Sanomat-Medium-Web'}
              fontWeight={500}
              sx={{ fontSize: '1.56rem' }}
              style={{
                marginBottom: '16px',
                lineHeight: '1rem',
                height: '25px',
                fontWeight: '500',
                fontStretch: 'normal',
                fontStyle: 'normal',
                letterSpacing: 'normal',
              }}
            >
              DSCSA - Traceability Report
            </Typography>
            <Typography
              variant="h6"
              align="center"
              gutterBottom
              color="primary.dark"
              fontFamily={'Sanomat-Medium-Web'}
              fontWeight={500}
              sx={{ fontSize: '1.56rem' }}
              style={{ marginBottom: '8px', lineHeight: '1rem' }}
              mb={2}
            >
              Verify Your Order
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box display="flex" alignItems="center" marginBottom={1}>
                <Controller
                  name="po"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      margin="normal"
                      fullWidth
                      autoComplete="off"
                      error={!!errors.po}
                      helperText={
                        errors.po ? errors.po.message : 'Enter characters exactly as shown.'
                      }
                      autoFocus
                      variant="outlined"
                      label="PO #*"
                      placeholder="PO123456"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          lineHeight: '1',
                        },
                        '& .MuiFormHelperText-root': {
                          color: 'primary.main',
                        },
                      }}
                      slotProps={{
                        htmlInput: {
                          style: { paddingTop: '12.5px', paddingBottom: '12.5px' },
                        },
                      }}
                      onChange={(e) => handlePOChange(e, field)}
                      // onKeyDown={(e) => {
                      //   setFullData((prev: { po: string; sscc: string }) => ({
                      //     ...prev,
                      //     po: `${prev?.po ? prev?.po + ',' + e.keyCode : e.keyCode}`,
                      //   }))
                      // }}
                    />
                  )}
                />
              </Box>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <Controller
                  name="sscc"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      InputLabelProps={{ shrink: true }}
                      placeholder="(00)00192010325423547"
                      margin="normal"
                      fullWidth
                      value={field.value}
                      autoComplete="off"
                      error={!!errors.sscc || characterCount > 22}
                      variant="outlined"
                      label="SSCC*"
                      sx={{
                        '& .MuiInputLabel-root': {
                          lineHeight: '1',
                        },
                        '& .MuiFormHelperText-root': {
                          color: 'primary.main',
                        },
                      }}
                      onFocus={() => {
                        if (field.value == '') {
                          field.onChange('(00)')
                        }
                      }}
                      style={{ marginBottom: '0px' }}
                      slotProps={{
                        htmlInput: {
                          style: { paddingTop: '12.5px', paddingBottom: '12.5px' },
                        },
                      }}
                      onChange={(e) => handleChange(e, field)}
                      // onKeyDown={(e) => {
                      //   setFullData((prev: { po: string; sscc: string }) => ({
                      //     ...prev,
                      //     sscc: `${prev?.sscc ? prev?.sscc + ',' + e.keyCode : e.keyCode}`,
                      //   }))
                      // }}
                    />
                  )}
                />
              </Box>
              <FormHelperText
                id="sscc-helper-text"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: errors.sscc || characterCount > 22 ? 'error.main' : 'primary.main',
                  marginTop: '-15px',
                  marginLeft: '15px',
                  width: 'calc(100% -15px)',
                }}
              >
                <span>Enter numbers exactly as shown.</span>
                <span>{characterCount}/22</span>
              </FormHelperText>
              <Stack spacing={2} alignItems={'center'} marginTop={5} flexDirection={'column'}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || isButtonDisabled}
                  sx={{
                    bgcolor: !isSubmitting ? 'primary.main' : 'grey.200',
                    color: !isSubmitting ? 'white' : 'text.primary',
                    borderRadius: '36px',
                    fontWeight: 400,
                    textTransform: 'uppercase',
                    fontSize: '.9rem',
                    padding: '8px 24px',
                    letterSpacing: '2px',
                    '&:hover': {
                      bgcolor: !isSubmitting ? 'primary.dark' : 'grey.300',
                    },
                  }}
                >
                  Verify
                </Button>
              </Stack>
              <Stack justifyContent={'center'}>
                <Link
                  onClick={() => {
                    setShowModal(true)
                    setErrorMessage({
                      header: 'Input Guidelines',
                      msg: '<strong>Purchase Order (PO)</strong><ul><li><strong>Where to Find It:</strong> On the Master Packing Slip or Shipping Label (e.g., PO123456).</li><br><li><strong>Allowed Characters:</strong> Letters (A-Z, a-z), Numbers (0-9), and these symbols: & - _ . ; : / ( ) # Space.</li><br><li><strong>Not Allowed:</strong>  ‘ “ < ></li></ul><strong>Serial Shipping Container Code (SSCC)</strong><ul><li><strong>Where to Find It:</strong> On the Master Packing Slip or Shipping Label (e.g., (00)001982010326523547).</li><br><li><strong>Important:</strong> This unique ID is always 22 characters including the leading (00).</li></ul>',
                      btnText: 'CLOSE',
                    })
                  }}
                  sx={{
                    marginTop: '20px',
                    mb: '20px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontFamily: 'Atlas-Grotesk-Web-Regular',
                    fontSize: '16px',
                  }}
                >
                  Input Guidelines
                </Link>
              </Stack>
            </form>
          </Paper>
        </Box>
        <Stack
          display={'flex'}
          flexShrink={0}
          direction={{ xs: 'row', sm: 'row' }}
          spacing={{ xs: 2, sm: 8 }}
          sx={{ flexWrap: 'wrap' }}
        >
          <Link
            sx={{
              '&:hover': { color: 'primary.dark' },
              fontFamily: 'Atlas-Grotesk-Web-Regular',
              fontWeight: '400',
              fontSize: '.8rem',
            }}
            target="_blank"
            href="https://www.lslog.com/terms/"
          >
            Terms of Use
          </Link>
          <Link
            sx={{
              '&:hover': { color: 'primary.dark' },
              fontFamily: 'Atlas-Grotesk-Web-Regular',
              fontWeight: '400',
              fontSize: '.8rem',
            }}
            target="_blank"
            href="https://www.lslog.com/privacy-policy/"
          >
            Privacy Policy
          </Link>
          <Link
            sx={{
              '&:hover': { color: 'primary.dark' },
              fontFamily: 'Atlas-Grotesk-Web-Regular',
              fontWeight: '400',
              fontSize: '.8rem',
            }}
            target="_blank"
            href="https://www.lslog.com/trademarks/"
          >
            Trademarks
          </Link>
        </Stack>
      </Stack>
      <AlertModal
        content={errorMessage}
        open={showModal}
        handleClose={() => {
          setShowModal(false)
          setErrorMessage({
            msg: '',
            header: '',
            btnText: '',
          })
        }}
      />
    </Container>
  )
}

export default Login
